import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    /* ── Host ──────────────────────────────────────────────────────────────── */
    :host {
      display: block;
      --uwc-dt-header-bg:        transparent;
      --uwc-dt-header-color:     ${t.text};
      --uwc-dt-header-weight:    ${t.fontWeightSemibold};
      --uwc-dt-border:           ${t.border};
      --uwc-dt-text-color:       ${t.text};
      --uwc-dt-text-secondary:   ${t.textSecondary};
      --uwc-dt-row-hover-bg:     ${t.hoverBg};
      --uwc-dt-stripe-bg:        rgba(0, 0, 0, 0.025);
      --uwc-dt-body-bg:          ${t.surface};
      --uwc-dt-footer-bg:        transparent;
      --uwc-dt-radius:           ${t.radiusMd};
      --uwc-dt-cell-padding-x:   ${t.space4};
      --uwc-dt-cell-padding-y:   ${t.space3};
      --uwc-dt-font-size:        ${t.fontSizeMd};
      --uwc-dt-empty-color:      ${t.textMuted};
      --uwc-dt-caption-color:    ${t.textSecondary};
      --uwc-dt-mask-bg:          rgba(255, 255, 255, 0.72);
    }

    /* ── Wrapper ────────────────────────────────────────────────────────────── */
    .uwc-dt__wrapper {
      position: relative;
      width: 100%;
      overflow-x: auto;
      /* No left/right border, no border-radius — matches PrimeNG's flat table style */
      border-top:    1px solid var(--uwc-dt-border);
      border-bottom: 1px solid var(--uwc-dt-border);
      background: var(--uwc-dt-body-bg);
    }

    /* When paginator sits at the bottom, the paginator's own border-top acts as
       the separator — remove the wrapper's bottom border to avoid a double line. */
    :host([paginator]) .uwc-dt__wrapper,
    :host([paginator][paginator-position="bottom"]) .uwc-dt__wrapper {
      border-bottom: none;
    }

    /* Paginator at top — remove wrapper's top border instead. */
    :host([paginator][paginator-position="top"]) .uwc-dt__wrapper {
      border-top:    none;
      border-bottom: 1px solid var(--uwc-dt-border);
    }

    /* Paginator on both sides — remove both wrapper borders. */
    :host([paginator][paginator-position="both"]) .uwc-dt__wrapper {
      border-top:    none;
      border-bottom: none;
    }

    /* ── Table ──────────────────────────────────────────────────────────────── */
    .uwc-dt__table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--uwc-dt-font-size);
      color: var(--uwc-dt-text-color);
      table-layout: auto;
    }

    /* ── Caption ────────────────────────────────────────────────────────────── */
    .uwc-dt__caption {
      text-align: left;
      padding: var(--uwc-dt-cell-padding-y) var(--uwc-dt-cell-padding-x);
      font-size: ${t.fontSizeSm};
      color: var(--uwc-dt-caption-color);
      caption-side: top;
    }

    /* ── Header ─────────────────────────────────────────────────────────────── */
    .uwc-dt__thead {
      background: var(--uwc-dt-header-bg);
    }

    .uwc-dt__th {
      text-align: left;
      padding: var(--uwc-dt-cell-padding-y) var(--uwc-dt-cell-padding-x);
      font-weight: var(--uwc-dt-header-weight);
      color: var(--uwc-dt-header-color);
      border-bottom: 1px solid var(--uwc-dt-border);
      white-space: nowrap;
      vertical-align: bottom;
      user-select: none;
    }

    /* ── Body ───────────────────────────────────────────────────────────────── */
    .uwc-dt__td {
      padding: var(--uwc-dt-cell-padding-y) var(--uwc-dt-cell-padding-x);
      border-bottom: 1px solid var(--uwc-dt-border);
      vertical-align: middle;
    }

    .uwc-dt__tbody tr:last-child .uwc-dt__td {
      border-bottom: none;
    }

    .uwc-dt__tbody tr {
      transition: background-color ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__tbody tr:hover {
      background: var(--uwc-dt-row-hover-bg);
    }

    /* ── Footer ─────────────────────────────────────────────────────────────── */
    .uwc-dt__tfoot {
      background: var(--uwc-dt-footer-bg);
    }

    .uwc-dt__td--footer {
      border-top: 1px solid var(--uwc-dt-border);
      border-bottom: none;
      font-weight: ${t.fontWeightMedium};
      color: var(--uwc-dt-text-secondary);
    }

    /* ── Empty state ────────────────────────────────────────────────────────── */
    .uwc-dt__empty {
      text-align: center;
      padding: 2.5rem var(--uwc-dt-cell-padding-x);
      color: var(--uwc-dt-empty-color);
      font-size: ${t.fontSizeSm};
    }

    /* ── Size: small ────────────────────────────────────────────────────────── */
    :host([size="small"]) {
      --uwc-dt-cell-padding-x: ${t.space2};
      --uwc-dt-cell-padding-y: 0.375rem;
      --uwc-dt-font-size:      ${t.fontSizeSm};
    }

    /* ── Size: large ────────────────────────────────────────────────────────── */
    :host([size="large"]) {
      --uwc-dt-cell-padding-x: ${t.space5};
      --uwc-dt-cell-padding-y: ${t.space4};
      --uwc-dt-font-size:      ${t.fontSizeLg};
    }

    /* ── Grid lines ─────────────────────────────────────────────────────────── */
    :host([show-gridlines]) .uwc-dt__wrapper {
      border: 1px solid var(--uwc-dt-border);
    }

    :host([show-gridlines]) .uwc-dt__table {
      border-collapse: collapse;
    }

    :host([show-gridlines]) .uwc-dt__th,
    :host([show-gridlines]) .uwc-dt__td {
      border: 1px solid var(--uwc-dt-border);
    }

    :host([show-gridlines]) .uwc-dt__tbody tr:last-child .uwc-dt__td {
      border-bottom: 1px solid var(--uwc-dt-border);
    }

    /* ── Striped rows ───────────────────────────────────────────────────────── */
    :host([striped-rows]) .uwc-dt__tbody tr:nth-child(even) {
      background: var(--uwc-dt-stripe-bg);
    }

    :host([striped-rows]) .uwc-dt__tbody tr:nth-child(even):hover {
      background: var(--uwc-dt-row-hover-bg);
    }

    /* ── Loading mask overlay ───────────────────────────────────────────────── */
    .uwc-dt__mask {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--uwc-dt-mask-bg);
      border-radius: var(--uwc-dt-radius);
      z-index: 1;
      backdrop-filter: blur(1px);
    }

    .uwc-dt__mask-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 50%;
      background: ${t.surface};
      box-shadow: ${t.shadowMd};
      color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Skeleton shimmer ───────────────────────────────────────────────────── */
    .uwc-dt__skel {
      height: 1rem;
      border-radius: ${t.radiusSm};
      background: linear-gradient(
        90deg,
        ${t.borderSubtle} 25%,
        ${t.border}       50%,
        ${t.borderSubtle} 75%
      );
      background-size: 200% 100%;
      animation: uwc-dt-shimmer 1.4s ease infinite;
    }

    @keyframes uwc-dt-shimmer {
      0%   { background-position:  200% 0; }
      100% { background-position: -200% 0; }
    }

    /* Skeleton rows — disable hover effect */
    .uwc-dt__tbody tr.uwc-dt__row--skeleton:hover {
      background: transparent;
    }

    /* ── Paginator integration ──────────────────────────────────────────────── */
    /* Override the paginator's own border-radius so it looks flush with the table.
       The paginator's built-in border-top serves as the visual separator. */
    .uwc-dt__paginator {
      --uwc-paginator-radius: 0;
    }

    /* Paginator above the table — swap border direction. */
    .uwc-dt__paginator--top {
      --uwc-paginator-border: none;
      border-bottom: 1px solid var(--uwc-dt-border);
    }

    /* ── Slot areas ─────────────────────────────────────────────────────────── */
    ::slotted([slot="header"]),
    ::slotted([slot="footer"]) {
      display: block;
    }

    /* ── Sortable column header ─────────────────────────────────────────────── */
    .uwc-dt__th--sortable {
      cursor: pointer;
    }

    .uwc-dt__th--sortable:hover {
      color: var(--uwc-color-primary, ${t.primary});
    }

    .uwc-dt__th--sortable:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    .uwc-dt__th-content {
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
    }

    .uwc-dt__sort-icon {
      flex-shrink: 0;
      color: ${t.textSecondary};
      opacity: 0.35;
      transition: color ${t.durationFast} ${t.easingOut}, opacity ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__sort-icon--active {
      color: var(--uwc-color-primary, ${t.primary});
      opacity: 1;
    }

    .uwc-dt__sort-rank {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1rem;
      height: 1rem;
      padding: 0 0.2rem;
      border-radius: ${t.radiusFull};
      background: var(--uwc-color-primary, ${t.primary});
      color: ${t.colorWhite};
      font-size: ${t.fontSizeXs};
      font-weight: ${t.fontWeightBold};
      line-height: 1;
    }

    /* ── Filter row (basic mode) ────────────────────────────────────────────── */
    .uwc-dt__filter-row .uwc-dt__th--filter {
      padding: 0.25rem var(--uwc-dt-cell-padding-x);
      border-bottom: 1px solid var(--uwc-dt-border);
    }

    .uwc-dt__th--filter-empty {
      padding: 0;
      border-bottom: 1px solid var(--uwc-dt-border);
    }

    /* ── Filter button (menu mode) ──────────────────────────────────────────── */
    .uwc-dt__filter-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      /* font-size drives the em-based box AND the funnel icon (sized in em), so
         the whole trigger rescales with the size/font presets instead of being
         pinned to the <button> UA default. */
      font-size: ${t.fontSizeMd};
      width: 1.7em;
      height: 1.7em;
      padding: 0;
      border: none;
      border-radius: ${t.radiusSm};
      background: transparent;
      cursor: pointer;
      color: ${t.textSecondary};
      opacity: 0.4;
      transition:
        opacity    ${t.durationFast} ${t.easingOut},
        color      ${t.durationFast} ${t.easingOut},
        background ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__filter-btn:hover {
      background: ${t.hoverBg};
      opacity: 1;
    }

    .uwc-dt__filter-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }

    .uwc-dt__filter-btn--active {
      color: var(--uwc-color-primary, ${t.primary});
      opacity: 1;
    }

    /* ── Filter panel (inside uwc-overlay content slot) ─────────────────────── */
    .uwc-dt__filter-panel {
      padding: ${t.space3};
      min-width: 14rem;
    }

    /* ── Global filter bar (menu mode, above table wrapper) ─────────────────── */
    .uwc-dt__global-filter {
      padding-bottom: ${t.space3};
    }

    /* ── Text-filter row input group ───────────────────────────────────────── */
    .uwc-dt__filter-input-group {
      display: flex;
      gap: 0.25rem;
      align-items: center;
    }

    /* ── Text-filter match-mode button (funnel trigger in row mode) ─────────── */
    .uwc-dt__filter-mode-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      /* em-based box + font-size so the funnel icon (sized in em) rescales with
         the size/font presets rather than the <button> UA default. */
      font-size: ${t.fontSizeMd};
      width: 2em;
      height: 2em;
      padding: 0;
      border: none;
      border-radius: ${t.radiusSm};
      background: transparent;
      cursor: pointer;
      flex-shrink: 0;
      color: ${t.textMuted};
      transition: color ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__filter-mode-btn--active {
      color: var(--uwc-color-primary, ${t.primary});
    }

    /* ── Hidden default slot (column descriptor children) ───────────────────── */
    .uwc-dt__col-slot {
      display: none;
    }

    /* ── Selection columns (radio / checkbox) ─────────────────────────────── */
    .uwc-dt__th--radio,
    .uwc-dt__th--checkbox,
    .uwc-dt__td--radio,
    .uwc-dt__td--checkbox {
      width: 3rem;
      text-align: center;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    /* Row with a selection column — clicking the row itself doesn't select */
    :host([selection-mode]) .uwc-dt__tbody tr.uwc-dt__row--sel-col {
      cursor: default;
      user-select: auto;
    }

    /* Only the cell containing the control is interactive */
    .uwc-dt__td--radio,
    .uwc-dt__td--checkbox {
      cursor: pointer;
      user-select: none;
    }

    /* ── Row selection ──────────────────────────────────────────────────────── */
    :host([selection-mode]) .uwc-dt__tbody tr {
      cursor: pointer;
      user-select: none;
    }

    .uwc-dt__tbody tr.uwc-dt__row--selected {
      background: var(--uwc-dt-selected-bg, ${t.selectedBg}) !important;
    }

    .uwc-dt__tbody tr.uwc-dt__row--selected:hover {
      background: var(--uwc-dt-selected-hover-bg, rgba(99, 102, 241, 0.13)) !important;
    }

    /* Keyboard focus ring for selectable rows */
    :host([selection-mode]) .uwc-dt__tbody tr:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    /* ── Expander column ────────────────────────────────────────────────────── */
    .uwc-dt__th--expander,
    .uwc-dt__td--expander {
      width: 3rem;
      text-align: center;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }

    /* ── Expansion toggle button ─────────────────────────────────────────────── */
    .uwc-dt__expansion-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;
      color: var(--uwc-dt-text-color);
      transition:
        background-color ${t.durationFast} ${t.easingOut},
        color            ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__expansion-toggle:hover:not(:disabled) {
      background: var(--uwc-dt-row-hover-bg);
      color: var(--uwc-color-primary, ${t.primary});
    }

    .uwc-dt__expansion-toggle:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }

    .uwc-dt__expansion-toggle:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    /* ── Expanded content row ───────────────────────────────────────────────── */
    .uwc-dt__td--expansion {
      padding: 0;
      border-bottom: 1px solid var(--uwc-dt-border);
      background: var(--uwc-dt-expansion-bg, var(--uwc-dt-body-bg));
    }

    /* Prevent hover tint bleeding into the expansion cell */
    .uwc-dt__row--expansion:hover > .uwc-dt__td--expansion {
      background: var(--uwc-dt-expansion-bg, var(--uwc-dt-body-bg));
    }

    /* ── Expansion body — animated wrapper ──────────────────────────────────── */
    .uwc-dt__expansion-body {
      padding: var(--uwc-dt-cell-padding-y) var(--uwc-dt-cell-padding-x);
      animation: uwc-dt-expand-in 200ms ${t.easingOut} forwards;
    }

    .uwc-dt__expansion-body--closing {
      animation: uwc-dt-expand-out 150ms ${t.easingOut} forwards;
    }

    @keyframes uwc-dt-expand-in {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0);    }
    }

    @keyframes uwc-dt-expand-out {
      from { opacity: 1; transform: translateY(0);    }
      to   { opacity: 0; transform: translateY(-6px); }
    }

    /* ── Reduce motion ──────────────────────────────────────────────────────── */
    @media (prefers-reduced-motion: reduce) {
      .uwc-dt__skel { animation: none; }
      .uwc-dt__tbody tr { transition: none; }
      .uwc-dt__sort-icon { transition: none; }
      .uwc-dt__filter-btn { transition: none; }
      .uwc-dt__filter-mode-btn { transition: none; }
      .uwc-dt__expansion-toggle { transition: none; }
      .uwc-dt__expansion-body,
      .uwc-dt__expansion-body--closing { animation: none; }
    }

    /* ═══════════════════════════════════════════════════════════════════════
       INLINE EDITING
       ═══════════════════════════════════════════════════════════════════════ */

    /* ── Editable cell (view mode) ──────────────────────────────────────────── */
    .uwc-dt__td--editable {
      cursor: pointer;
      position: relative;
    }

    .uwc-dt__td--editable:hover {
      background: var(--uwc-dt-row-hover-bg);
    }

    /* Pencil hint shown on hover (cell-edit mode) */
    .uwc-dt__cell-edit-icon {
      opacity: 0;
      margin-left: 0.375rem;
      color: ${t.textMuted};
      vertical-align: middle;
      flex-shrink: 0;
      transition: opacity ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__td--editable:hover .uwc-dt__cell-edit-icon,
    .uwc-dt__td--editable:focus-visible .uwc-dt__cell-edit-icon {
      opacity: 1;
    }

    .uwc-dt__td--editable:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: -2px;
    }

    /* ── Editing cell (active input) ────────────────────────────────────────── */
    .uwc-dt__td--editing {
      padding: calc(var(--uwc-dt-cell-padding-y) * 0.5) calc(var(--uwc-dt-cell-padding-x) * 0.5);
      background: var(--uwc-dt-body-bg);
    }

    /* Editing row highlight */
    .uwc-dt__row--editing > .uwc-dt__td {
      background: var(--uwc-dt-edit-row-bg, rgba(99, 102, 241, 0.04));
    }

    .uwc-dt__row--editing > .uwc-dt__td--editing {
      background: var(--uwc-dt-edit-row-bg, rgba(99, 102, 241, 0.04));
    }

    /* ── Default cell input ─────────────────────────────────────────────────── */
    .uwc-dt__cell-editor {
      display: block;
      width: 100%;
      min-width: 4rem;
      padding: 0.25rem 0.5rem;
      border: 1px solid var(--uwc-color-primary, ${t.primary});
      border-radius: ${t.radiusSm};
      background: ${t.surface};
      color: var(--uwc-dt-text-color);
      font-size: var(--uwc-dt-font-size);
      font-family: inherit;
      line-height: 1.4;
      outline: none;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
      transition: border-color ${t.durationFast} ${t.easingOut},
                  box-shadow   ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__cell-editor:focus {
      border-color: var(--uwc-color-primary, ${t.primary});
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
    }

    /* ── Row-editor column ──────────────────────────────────────────────────── */
    .uwc-dt__th--row-editor,
    .uwc-dt__td--row-editor {
      width: 5rem;
      text-align: center;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      white-space: nowrap;
    }

    .uwc-dt__row-editor-controls {
      display: inline-flex;
      gap: 0.25rem;
      align-items: center;
    }

    .uwc-dt__row-editor-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      padding: 0;
      border: none;
      border-radius: ${t.radiusSm};
      background: transparent;
      cursor: pointer;
      color: ${t.textSecondary};
      transition:
        background-color ${t.durationFast} ${t.easingOut},
        color            ${t.durationFast} ${t.easingOut};
    }

    .uwc-dt__row-editor-btn:hover:not(:disabled) {
      background: var(--uwc-dt-row-hover-bg);
    }

    .uwc-dt__row-editor-btn:focus-visible {
      outline: 2px solid var(--uwc-color-primary, ${t.primary});
      outline-offset: 1px;
    }

    .uwc-dt__row-editor-btn:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }

    .uwc-dt__row-editor-btn--save:hover:not(:disabled) {
      color: ${t.success};
    }

    .uwc-dt__row-editor-btn--cancel:hover:not(:disabled) {
      color: ${t.danger};
    }

    .uwc-dt__row-editor-btn--init:hover:not(:disabled) {
      color: var(--uwc-color-primary, ${t.primary});
    }

    /* Reduce motion — edit transitions */
    @media (prefers-reduced-motion: reduce) {
      .uwc-dt__cell-editor,
      .uwc-dt__cell-edit-icon,
      .uwc-dt__row-editor-btn { transition: none; }
    }
  `,
];
